import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { profilesAPI } from '../utils/api';
import api from '../utils/api';
import { SCANNER_COMMANDS, buildBarcodeContent } from '../utils/scannerCommands';
import JsBarcode from 'jsbarcode';
import {
  FolderOpen, Plus, Trash2, Copy, Star, ChevronRight, X,
  ScanBarcode, AlertCircle, Printer, Pencil, CheckCircle, Users
} from 'lucide-react';

// ===== Barcode generation from profile settings =====
function profileToBarcodes(settings) {
  const barcodes = [];
  const entries = Object.entries(settings).sort((a, b) => (a[1]?.order ?? 0) - (b[1]?.order ?? 0));
  for (const [key, val] of entries) {
    if (!val || typeof val !== 'object') continue;
    const operand = parseInt(val.operand, 16) || parseInt(val.operand);
    if (!operand && operand !== 0) continue;
    let data = [];
    let label = key;
    if (Array.isArray(val.value)) {
      data = val.value.map(v => typeof v === 'string' ? parseInt(v, 16) : v);
      label = `${key}: [${data.map(d => '0x' + d.toString(16).padStart(2, '0')).join(', ')}]`;
    } else if (val.value !== undefined) {
      const v = typeof val.value === 'string' ? parseInt(val.value, 16) : val.value;
      data = [v];
      label = `${key}: ${val.mode || val.type || val.level || val.rate || '0x' + v.toString(16).padStart(2, '0').toUpperCase()}`;
    } else continue;
    try {
      barcodes.push({ content: buildBarcodeContent(operand, data), label, sublabel: `Op 0x${operand.toString(16).padStart(2, '0').toUpperCase()}` });
    } catch (e) { /* skip */ }
  }
  return barcodes;
}

function printProfileBarcodes(profileName, settings) {
  const barcodes = profileToBarcodes(settings);
  if (barcodes.length === 0) return alert('No printable settings');
  const rendered = barcodes.map(bc => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    try { JsBarcode(svg, bc.content, { format: 'CODE128', width: 2, height: 60, displayValue: false, margin: 10, background: '#fff', lineColor: '#000' }); } catch {}
    return { svgHtml: svg.outerHTML, label: bc.label, sublabel: bc.sublabel };
  });
  const html = rendered.map((item, i) => `<div class="bi"><div class="bn">${i+1}</div>${item.svgHtml}<p class="bl">${item.label}</p><p class="bs">${item.sublabel}</p></div>`).join('');
  const w = window.open('', '_blank');
  w.document.write(`<!DOCTYPE html><html><head><title>SophistiScan — ${profileName}</title><style>body{font-family:monospace;text-align:center;padding:20px}.bi{display:block;margin:25px auto;padding-bottom:20px;border-bottom:1px dashed #ccc;page-break-inside:avoid;position:relative}.bi:last-child{border-bottom:none}.bn{position:absolute;left:10px;top:10px;background:#333;color:#fff;width:24px;height:24px;border-radius:50%;font-size:12px;line-height:24px;text-align:center}.bl{font-weight:bold;font-size:13px;margin:5px 0 2px}.bs{color:#666;font-size:11px;margin:0}@media print{body{padding:0}.bn{background:#000}}</style></head><body><h2>SophistiScan SQ400H</h2><p style="color:#666">Profile: ${profileName} — Scan in order 1→${rendered.length}</p><hr/>${html}<script>setTimeout(()=>window.print(),300)<\/script></body></html>`);
  w.document.close();
}

// ===== Available settings for profile builder =====
const PROFILE_SETTINGS = Object.entries(SCANNER_COMMANDS)
  .filter(([, cmd]) => !cmd.type?.startsWith('symbology') && !cmd.warning && !cmd.data && cmd.operand)
  .map(([key, cmd]) => ({ key, ...cmd }));

// ===== Main Component =====
export default function ProfilesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isHe = i18n.language === 'he';
  const isAdmin = user?.role === 'admin';

  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);

  useEffect(() => { loadProfiles(); if (isAdmin) loadUsers(); }, []);

  const loadProfiles = async () => {
    try { const { data } = await profilesAPI.list(); setProfiles(data); }
    catch { setError('Failed to load profiles'); }
    finally { setLoading(false); }
  };

  const loadUsers = async () => {
    try { const { data } = await api.get('/profiles/users/list'); setUsers(data); }
    catch { /* ignore */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isHe ? 'למחוק את הפרופיל?' : 'Delete this profile?')) return;
    try {
      await profilesAPI.delete(id);
      setProfiles(prev => prev.filter(p => p.id !== id));
      setSuccess(isHe ? 'הפרופיל נמחק' : 'Profile deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Failed to delete'); }
  };

  const handleDuplicate = async (profile) => {
    try {
      const { data } = await profilesAPI.create({ name: `${profile.name} (Copy)`, description: profile.description, settings: profile.settings });
      setProfiles(prev => [data, ...prev]);
    } catch { setError('Failed to duplicate'); }
  };

  const handleSave = async (formData) => {
    try {
      if (editingProfile) {
        await profilesAPI.update(editingProfile.id, formData);
      } else {
        await profilesAPI.create(formData);
      }
      setShowModal(false);
      setEditingProfile(null);
      loadProfiles();
      setSuccess(isHe ? 'הפרופיל נשמר' : 'Profile saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      throw err; // let modal handle it
    }
  };

  const openCreate = () => { setEditingProfile(null); setShowModal(true); };
  const openEdit = (profile) => { setEditingProfile(profile); setShowModal(true); };

  const defaultProfiles = profiles.filter(p => p.isDefault);
  const myProfiles = profiles.filter(p => !p.isDefault && p.isOwner);
  const sharedWithMe = profiles.filter(p => !p.isDefault && !p.isOwner);
  const otherProfiles = profiles.filter(p => !p.isDefault && !p.isOwner);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-brand-500/30 border-t-brand-500 rounded-full animate-spin" /></div>;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 style={{color:'var(--text-primary)'}} className="text-2xl font-bold flex items-center gap-3">
            <FolderOpen className="w-7 h-7 text-cyan-400" />
            {t('profiles.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {isHe ? 'צור פרופילי הגדרות והדפס ברקודים בלחיצה' : 'Create setting profiles and print barcodes in one click'}
          </p>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {isHe ? 'פרופיל חדש' : 'New Profile'}
        </button>
      </div>

      {error && <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-4 text-red-400 text-sm"><AlertCircle className="w-4 h-4" />{error}</div>}
      {success && <div className="flex items-center gap-2 px-4 py-3 bg-green-500/10 border border-green-500/20 rounded-lg mb-4 text-green-400 text-sm"><CheckCircle className="w-4 h-4" />{success}</div>}

      {/* Default */}
      {defaultProfiles.length > 0 && (
        <Section title={isHe ? 'פרופילים מובנים' : 'Built-in Profiles'} icon={<Star className="w-4 h-4" />}>
          {defaultProfiles.map(p => <ProfileCard key={p.id} profile={p} isHe={isHe} isAdmin={isAdmin}
            canDelete={isAdmin} canEdit={isAdmin}
            onPrint={() => printProfileBarcodes(p.name, p.settings)} onDuplicate={() => handleDuplicate(p)}
            onDelete={() => handleDelete(p.id)} onEdit={() => openEdit(p)} />)}
        </Section>
      )}

      {/* My profiles */}
      <Section title={isHe ? 'הפרופילים שלי' : 'My Profiles'} icon={<ScanBarcode className="w-4 h-4" />}>
        {myProfiles.length === 0 ? (
          <div className="card p-10 text-center">
            <FolderOpen className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">{isHe ? 'עדיין אין לך פרופילים. לחץ "פרופיל חדש" ליצירה.' : 'No profiles yet. Click "New Profile" to create one.'}</p>
          </div>
        ) : myProfiles.map(p => <ProfileCard key={p.id} profile={p} isHe={isHe} isAdmin={isAdmin} canDelete canEdit
          onPrint={() => printProfileBarcodes(p.name, p.settings)} onDuplicate={() => handleDuplicate(p)}
          onDelete={() => handleDelete(p.id)} onEdit={() => openEdit(p)} />)}
      </Section>

      {/* Shared / assigned profiles */}
      {!isAdmin && sharedWithMe.length > 0 && (
        <Section title={isHe ? 'פרופילים ששויכו אלי' : 'Shared With Me'} icon={<Users className="w-4 h-4" />}>
          {sharedWithMe.map(p => <ProfileCard key={p.id} profile={p} isHe={isHe} isAdmin={isAdmin} showOwner
            onPrint={() => printProfileBarcodes(p.name, p.settings)} onDuplicate={() => handleDuplicate(p)} />)}
        </Section>
      )}

      {/* Admin: other users' profiles */}
      {isAdmin && otherProfiles.length > 0 && (
        <Section title={isHe ? 'פרופילים של משתמשים אחרים' : 'Other Users\' Profiles'} icon={<Users className="w-4 h-4" />}>
          {otherProfiles.map(p => <ProfileCard key={p.id} profile={p} isHe={isHe} isAdmin={isAdmin} canDelete canEdit showOwner
            onPrint={() => printProfileBarcodes(p.name, p.settings)} onDuplicate={() => handleDuplicate(p)}
            onDelete={() => handleDelete(p.id)} onEdit={() => openEdit(p)} />)}
        </Section>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <ProfileModal
          isHe={isHe}
          isAdmin={isAdmin}
          users={users}
          profile={editingProfile}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingProfile(null); }}
        />
      )}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-sm font-semibold uppercase tracking-wider mb-3 flex items-center gap-2" style={{color:'var(--text-secondary)'}}>{icon}{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

// ===== Profile Card =====
function ProfileCard({ profile, isHe, isAdmin, canDelete, canEdit, showOwner, onPrint, onDuplicate, onDelete, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const settings = profile.settings || {};
  const count = Object.keys(settings).length;

  return (
    <div className="card-hover">
      <div className="p-5 flex items-center gap-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="w-10 h-10 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center shrink-0">
          <ScanBarcode className="w-5 h-5 text-brand-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold" style={{color:'var(--text-primary)'}}>{profile.name}</h3>
            {profile.isDefault && <span className="badge-blue">{isHe ? 'מובנה' : 'Default'}</span>}
            {isAdmin && profile.visibility === 'all' && <span className="badge-green">{isHe ? 'לכולם' : 'All Users'}</span>}
            {isAdmin && profile.visibility === 'assigned' && <span className="badge-amber">{isHe ? `${profile.assignedUsers?.length || 0} משתמשים` : `${profile.assignedUsers?.length || 0} users`}</span>}
            <span className="badge" style={{background:'var(--bg-input)', color:'var(--text-muted)', border:'1px solid var(--border)'}}>{count} {isHe ? 'הגדרות' : 'settings'}</span>
          </div>
          {profile.description && <p className="text-sm mt-0.5 truncate" style={{color:'var(--text-secondary)'}}>{profile.description}</p>}
          {isAdmin && showOwner && <p className="text-xs mt-0.5" style={{color:'var(--text-muted)'}}>{isHe ? 'שייך ל:' : 'Owner:'} {profile.ownerName} ({profile.ownerEmail})</p>}
          {isAdmin && profile.assignedUsers?.length > 0 && (
            <p className="text-xs mt-0.5" style={{color:'var(--text-muted)'}}>
              {isHe ? 'משויך ל:' : 'Assigned to:'} {profile.assignedUsers.map(u => u.displayName).join(', ')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={e => { e.stopPropagation(); onPrint?.(); }} className="btn-ghost text-green-400" title={isHe ? 'הדפס' : 'Print'}><Printer className="w-4 h-4" /></button>
          {canEdit && <button onClick={e => { e.stopPropagation(); onEdit?.(); }} className="btn-ghost text-blue-400" title={isHe ? 'ערוך' : 'Edit'}><Pencil className="w-4 h-4" /></button>}
          <button onClick={e => { e.stopPropagation(); onDuplicate?.(); }} className="btn-ghost" title={isHe ? 'שכפל' : 'Duplicate'}><Copy className="w-4 h-4" /></button>
          {canDelete && <button onClick={e => { e.stopPropagation(); onDelete?.(); }} className="btn-ghost text-red-400" title={isHe ? 'מחק' : 'Delete'}><Trash2 className="w-4 h-4" /></button>}
          <ChevronRight className={`w-5 h-5 text-gray-600 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </div>
      {expanded && (
        <div className="px-5 pb-5 border-t border-[#1e2736] pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-400">{isHe ? 'הגדרות' : 'Settings'}</h4>
            <button onClick={() => onPrint?.()} className="btn-primary text-xs flex items-center gap-1.5"><Printer className="w-3.5 h-3.5" />{isHe ? 'הדפס ברקודים' : 'Print Barcodes'}</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {Object.entries(settings).sort((a, b) => (a[1]?.order ?? 0) - (b[1]?.order ?? 0)).map(([key, val]) => (
              <div key={key} className="flex items-start gap-3 px-3 py-2.5 bg-black/20 rounded-lg">
                <ScanBarcode className="w-4 h-4 text-brand-500/50 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-300 capitalize">{key.replace(/_/g, ' ')}</span>
                  {val.description && (
                    <p className="text-xs mt-0.5 font-medium" style={{color:'var(--brand)'}}>{val.description}</p>
                  )}
                  <span className="block text-xs text-gray-600 font-mono truncate mt-0.5">{val.mode || val.type || val.level || val.rate || ''}{val.operand && ` • Op: ${val.operand}`}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Create/Edit Modal =====
function ProfileModal({ isHe, isAdmin, users, profile, onSave, onClose }) {
  const [name, setName] = useState(profile?.name || '');
  const [description, setDescription] = useState(profile?.description || '');
  const [visibility, setVisibility] = useState(profile?.visibility || 'private');
  const [selectedUsers, setSelectedUsers] = useState(
    profile?.assignedUsers?.map(u => u.id) || []
  );
  const [settings, setSettings] = useState(profile?.settings || {});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const addSetting = (cmdKey, cmd, value) => {
    let settingValue;
    if (cmd.type === 'dual_byte') {
      settingValue = { operand: '0x' + cmd.operand.toString(16).padStart(2, '0'), value };
      const preset = cmd.presets?.find(p => p.value[0] === value[0] && p.value[1] === value[1]);
      if (preset) settingValue.type = preset.label;
    } else if (cmd.type === 'range') {
      settingValue = { operand: '0x' + cmd.operand.toString(16).padStart(2, '0'), value };
    } else {
      const opt = cmd.options?.find(o => o.value === value);
      settingValue = { operand: '0x' + cmd.operand.toString(16).padStart(2, '0'), value, mode: opt?.label };
    }
    const order = Object.keys(settings).length;
    settingValue.order = order;
    setSettings(prev => ({ ...prev, [cmd.name.replace(/\s+/g, '_').toLowerCase()]: settingValue }));
  };

  const removeSetting = (key) => {
    setSettings(prev => { const n = { ...prev }; delete n[key]; return n; });
  };

  const toggleUser = (uid) => {
    setSelectedUsers(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Object.keys(settings).length === 0) { setError(isHe ? 'צריך לפחות הגדרה אחת' : 'At least one setting required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name, description, settings,
        visibility: isAdmin ? visibility : 'private',
      };
      if (isAdmin && visibility === 'assigned') {
        payload.assignedUserIds = selectedUsers;
      }
      await onSave(payload);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center pt-8 md:pt-16 z-50 overflow-y-auto" onClick={onClose}>
      <div className="card w-full max-w-3xl mx-4 mb-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b" style={{borderColor:'var(--border)'}}>
          <h2 className="text-lg font-semibold" style={{color:'var(--text-primary)'}}>
            {profile ? (isHe ? 'עריכת פרופיל' : 'Edit Profile') : (isHe ? 'פרופיל חדש' : 'New Profile')}
          </h2>
          <button onClick={onClose} className="btn-ghost"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-5">
          {error && <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm"><AlertCircle className="w-4 h-4" />{error}</div>}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">{isHe ? 'שם הפרופיל' : 'Profile Name'}</label>
              <input type="text" className="input-field" value={name} onChange={e => setName(e.target.value)} required minLength={2} placeholder={isHe ? 'למשל: סופר רמי לוי' : 'e.g. Warehouse Scanner'} />
            </div>
            <div>
              <label className="label">{isHe ? 'תיאור' : 'Description'}</label>
              <input type="text" className="input-field" value={description} onChange={e => setDescription(e.target.value)} placeholder={isHe ? 'אופציונלי' : 'Optional'} />
            </div>
          </div>

          {/* Admin: visibility */}
          {isAdmin && (
            <div>
              <label className="label flex items-center gap-2">
                <Users className="w-4 h-4" />
                {isHe ? 'מי יראה את הפרופיל?' : 'Who can see this profile?'}
              </label>
              <div className="space-y-2">
                {[
                  { val: 'private', label: isHe ? 'רק אני' : 'Only me', desc: isHe ? 'פרופיל פרטי' : 'Private profile' },
                  { val: 'all', label: isHe ? 'כל המשתמשים' : 'All users', desc: isHe ? 'כולם יראו' : 'Visible to everyone' },
                  { val: 'assigned', label: isHe ? 'משתמשים ספציפיים' : 'Specific users', desc: isHe ? 'בחר למי' : 'Choose who' },
                ].map(opt => (
                  <label key={opt.val} className="flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer border transition-all"
                    style={{
                      borderColor: visibility === opt.val ? 'var(--brand)' : 'var(--border)',
                      background: visibility === opt.val ? 'var(--brand-light)' : 'transparent',
                    }}>
                    <input type="radio" name="vis" value={opt.val} checked={visibility === opt.val}
                      onChange={() => setVisibility(opt.val)} className="accent-brand-500" />
                    <div>
                      <span className="text-sm font-medium" style={{color:'var(--text-primary)'}}>{opt.label}</span>
                      <span className="block text-xs" style={{color:'var(--text-muted)'}}>{opt.desc}</span>
                    </div>
                  </label>
                ))}
              </div>

              {visibility === 'assigned' && users.length > 0 && (
                <div className="mt-3 max-h-48 overflow-y-auto rounded-lg border p-2 space-y-1" style={{borderColor:'var(--border)', background:'var(--bg-card)'}}>
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-brand-500/5 cursor-pointer">
                      <input type="checkbox" className="accent-brand-500"
                        checked={selectedUsers.includes(u.id)}
                        onChange={() => toggleUser(u.id)}
                      />
                      <span className="text-sm" style={{color:'var(--text-primary)'}}>{u.displayName}</span>
                      <span className="text-xs" style={{color:'var(--text-muted)'}}>({u.email})</span>
                    </label>
                  ))}
                  {selectedUsers.length > 0 && (
                    <p className="text-xs px-2 pt-1" style={{color:'var(--brand)'}}>
                      {isHe ? `${selectedUsers.length} משתמשים נבחרו` : `${selectedUsers.length} users selected`}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Current settings */}
          <div>
            <label className="label">{isHe ? 'הגדרות בפרופיל' : 'Profile Settings'} ({Object.keys(settings).length})</label>
            {Object.keys(settings).length === 0 ? (
              <p className="text-sm py-3" style={{color:'var(--text-muted)'}}>{isHe ? 'הוסף הגדרות מהרשימה למטה' : 'Add settings from the list below'}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
                {Object.entries(settings).sort((a, b) => (a[1]?.order ?? 0) - (b[1]?.order ?? 0)).map(([key, val]) => (
                  <div key={key} className="flex items-start gap-2 px-3 py-2 rounded-lg group" style={{background:'var(--bg-input)'}}>
                    <ScanBarcode className="w-4 h-4 text-brand-500/50 shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm capitalize" style={{color:'var(--text-primary)'}}>{key.replace(/_/g, ' ')}</span>
                      <span className="block text-xs font-mono" style={{color:'var(--text-muted)'}}>{val.mode || val.type || ''} {val.operand}</span>
                      <input
                        type="text"
                        className="input-field text-xs py-1 mt-1.5"
                        value={val.description || ''}
                        onChange={e => setSettings(prev => ({ ...prev, [key]: { ...prev[key], description: e.target.value } }))}
                        placeholder={isHe ? 'הסבר למשתמש...' : 'Explanation for user...'}
                        onClick={e => e.stopPropagation()}
                      />
                    </div>
                    <button type="button" onClick={() => removeSetting(key)} className="hover:text-red-400 opacity-0 group-hover:opacity-100 mt-0.5" style={{color:'var(--text-muted)'}}><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add settings */}
          <div>
            <label className="label">{isHe ? 'הוסף הגדרה' : 'Add Setting'}</label>
            <div className="max-h-64 overflow-y-auto space-y-2 rounded-lg p-3 border" style={{borderColor:'var(--border)', background:'var(--bg-input)'}}>
              {PROFILE_SETTINGS.map(cmd => (
                <SettingPicker key={cmd.key} cmd={cmd} isHe={isHe} onAdd={(val) => addSetting(cmd.key, cmd, val)} />
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
              {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {isHe ? 'שמור' : 'Save'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">{isHe ? 'ביטול' : 'Cancel'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===== Setting Picker Row =====
function SettingPicker({ cmd, isHe, onAdd }) {
  const [selectedValue, setSelectedValue] = useState(null);
  const name = isHe ? (cmd.nameHe || cmd.name) : cmd.name;

  const handleAdd = () => {
    if (selectedValue === null) return;
    onAdd(selectedValue);
    setSelectedValue(null);
  };

  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-sm text-gray-400 w-48 shrink-0 truncate" title={name}>{name}</span>
      <div className="flex-1">
        {cmd.options && (
          <select
            className="select-field text-xs py-1.5"
            value={selectedValue ?? ''}
            onChange={e => setSelectedValue(cmd.type === 'dual_byte' ? cmd.presets[parseInt(e.target.value)]?.value : parseInt(e.target.value))}
          >
            <option value="">{isHe ? 'בחר...' : 'Select...'}</option>
            {(cmd.presets || cmd.options).map((opt, i) => (
              <option key={i} value={cmd.presets ? i : opt.value}>
                {isHe ? (opt.labelHe || opt.label) : opt.label}
              </option>
            ))}
          </select>
        )}
        {cmd.type === 'range' && !cmd.options && (
          <input type="number" className="input-field text-xs py-1.5 w-24" min={cmd.min} max={cmd.max}
            value={selectedValue ?? ''} onChange={e => setSelectedValue(parseInt(e.target.value) || 0)} placeholder={`${cmd.min}-${cmd.max}`} />
        )}
      </div>
      <button type="button" onClick={handleAdd} disabled={selectedValue === null} className="btn-ghost text-brand-400 disabled:text-gray-700">
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}
